import { MenuItem } from "@chakra-ui/react";
import { AddEntityButton } from "features/entities/AddEntityButton";
import { useEntityConfirmDelete } from "features/entities/EntityDelete";
import { useFundEntityTableDataRetrieve } from "gen/endpoints/fund-entities-table-data/fund-entities-table-data";
import { useGlobalEntityTableDataRetrieve } from "gen/endpoints/global-entity-table-data/global-entity-table-data";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { LoadingState } from "shared/components/empty/LoadingState";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { DataTable } from "shared/components/table/data/DataTable";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";

export interface EntitiesTableProps {
  fundFamilyId?: string;
}

export const EntitiesTableContent: React.FC<EntitiesTableProps> = ({
  fundFamilyId,
}) => {
  const { t } = useTranslation(["entity"]);

  const { data: tableData, refetch: refetchTableData } =
    useEntityTableData(fundFamilyId);

  const confirmEntityDelete = useEntityConfirmDelete();
  const hasConfigPermission = useHasPermission(PermissionTypeEnum.config);

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => ({
      type: "menu",
      actions: (
        <Restricted permission={PermissionTypeEnum.config}>
          <MenuItem
            color="error.500"
            _hover={{ bg: "error.100" }}
            onClick={async () => {
              const success = await confirmEntityDelete(row.id!);
              if (success) {
                await refetchTableData();
              }
            }}
          >
            {t("entity:delete.title")}
          </MenuItem>
        </Restricted>
      ),
    }),
    [t, confirmEntityDelete, refetchTableData],
  );

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type !== "entity") {
        return undefined;
      }
      const functionalType = (
        row.data?.cells["functional_type"]?.value as
          | TextTableCellValue
          | undefined
      )?.text?.toLowerCase();
      if (
        functionalType === EntityFunctionalTypeEnum.investment &&
        metadata.fund_family_id
      ) {
        return {
          type: "generic",
          to: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId: metadata.fund_family_id })
            .investments({})
            .investment({ investmentId: metadata.id }).$,
        };
      }
      return functionalType == EntityFunctionalTypeEnum.investor
        ? {
            type: "generic",
            to: fundFamilyId
              ? rootRoute({})
                  .fundFamilies({})
                  .fundFamily({ fundFamilyId })
                  .setup({})
                  .investorEntities({})
                  .investorEntity({ investorEntityId: metadata.id })
                  .tabActivity({}).$
              : rootRoute({})
                  .investorEntities({})
                  .investorEntity({ investorEntityId: metadata.id })
                  .tabActivity({}).$,
          }
        : {
            type: "generic",
            to: fundFamilyId
              ? rootRoute({})
                  .fundFamilies({})
                  .fundFamily({ fundFamilyId })
                  .setup({})
                  .entities({})
                  .entity({ entityId: metadata.id }).$
              : rootRoute({}).entities({}).entity({ entityId: metadata.id }).$,
          };
    },
    [fundFamilyId],
  );

  return (
    <TableBoxBody>
      {tableData ? (
        <DataTable
          data={tableData}
          renderRowActions={hasConfigPermission ? renderRowActions : undefined}
          getLinkInfoForRow={getLinkInfoForRow}
          fillWidth={true}
          disablePinnedHeaderColumns={true}
          emptyStateProps={{
            title: t("entity:empty.title"),
            description: t("entity:empty.description"),
            children: <AddEntityButton fundFamilyId={fundFamilyId} />,
          }}
        />
      ) : (
        <LoadingState />
      )}
    </TableBoxBody>
  );
};

const useEntityTableData = (fundFamilyId?: string) => {
  const globalEntityTableDataResult = useGlobalEntityTableDataRetrieve(
    undefined,
    { query: { enabled: !fundFamilyId } },
  );
  const fundEntityTableDataResult = useFundEntityTableDataRetrieve(
    fundFamilyId!,
    undefined,
    { query: { enabled: !!fundFamilyId } },
  );
  return fundFamilyId ? fundEntityTableDataResult : globalEntityTableDataResult;
};
